import {
  Card,
  type CardProps,
  Divider,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import React from 'react';
import { memo } from 'react';
import {
  Iconify,
  Label,
  OneLineTypography,
  SvgIconStyle,
} from '@pflegenavi/web-components';
import {
  TIME_FORMAT,
  YEAR_MONTH_DAY_SHORT_FORMAT,
} from '@pflegenavi/shared/constants';
import { useTranslation } from 'react-i18next';
import {
  useFormatDate,
  useFormatTime,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import ServiceProviderSvgIcon from '../assets/service_provider_icon.svg';
import { PaymentStatus } from '@pflegenavi/shared/api';

export interface ReceiptBatchPaymentCardProps {
  payment: {
    id: string;
    amountInCents: number;
    initiationDate: Date;
    completedAt?: Date;
    status: PaymentStatus;
  };
  serviceProviderName?: string;
  nursingHomeName?: string;
  initiatorName?: string;
  sx?: CardProps['sx'];
  isLoading?: boolean;
  receiptAmountInCents?: number;
}

export const ReceiptBatchPaymentCard: FC<ReceiptBatchPaymentCardProps> = memo(
  ({ sx, ...props }) => {
    return (
      <Card sx={{ p: 2, ...sx }}>
        <Stack gap={2}>
          <Header />
          <Divider
            sx={{
              // Counter the card padding
              ml: -2,
              mr: -2,
            }}
          />
          <Content {...props} />
        </Stack>
      </Card>
    );
  }
);

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface HeaderProps {}

const Header: FC<HeaderProps> = () => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" gap={1.5} alignItems="center">
        <Stack
          sx={{
            backgroundColor: 'grey.300',
            borderRadius: 2,
          }}
          justifyContent="center"
          alignItems="center"
          height={40}
          width={40}
        >
          <Iconify width={24} height={24} icon={'mdi:receipt-text'} />
        </Stack>
        <Stack gap={0.5}>
          <OneLineTypography variant="subtitle2" fontWeight={700}>
            {t('receipts.batch.payment-card.title')}
          </OneLineTypography>
          <OneLineTypography fontSize={14} color="grey.600">
            {t('receipts.batch.payment-card.subtitle')}
          </OneLineTypography>
        </Stack>
      </Stack>
    </Stack>
  );
};

type ContentProps = Pick<
  ReceiptBatchPaymentCardProps,
  | 'payment'
  | 'nursingHomeName'
  | 'serviceProviderName'
  | 'initiatorName'
  | 'isLoading'
  | 'receiptAmountInCents'
>;

const Content: FC<ContentProps> = ({
  payment,
  nursingHomeName,
  serviceProviderName,
  initiatorName,
  isLoading,
  receiptAmountInCents,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const fTime = useFormatTime();

  return (
    <Stack gap={1.5}>
      <Row
        isLoading={isLoading}
        icon={<StatusIcon />}
        label={t('receipts.batch.payment-card.status-title')}
        value={<StatusLabel state={payment.status} />}
      />
      <Row
        isLoading={isLoading}
        icon={<CashIcon />}
        label={t('receipts.batch.payment-card.amount')}
        value={
          <Typography
            fontSize={14}
            color="text.primary"
            sx={{ fontWeight: 700 }}
            textAlign="right"
          >
            {fCurrencyCents(payment.amountInCents)}
          </Typography>
        }
      />
      {receiptAmountInCents && (
        <Row
          isLoading={isLoading}
          icon={<ReceiptIcon />}
          label={t('cashManagement.link.receipt-amount')}
          value={
            <Typography
              fontSize={14}
              color="text.primary"
              sx={{ fontWeight: 700 }}
              textAlign="right"
            >
              {fCurrencyCents(receiptAmountInCents)}
            </Typography>
          }
        ></Row>
      )}
      {serviceProviderName && (
        <Row
          isLoading={isLoading}
          icon={<ServiceProviderIcon />}
          label={t('receipts.batch.payment-card.service-provider')}
          value={
            <Typography fontSize={14} color="text.primary" textAlign="right">
              {serviceProviderName}
            </Typography>
          }
        />
      )}
      {nursingHomeName && (
        <Row
          isLoading={isLoading}
          icon={<NursingHomeIcon />}
          label={t('receipts.batch.payment-card.nursing-home')}
          value={
            <Typography fontSize={14} color="text.primary" textAlign="right">
              {nursingHomeName}
            </Typography>
          }
        />
      )}
      {initiatorName && (
        <Row
          isLoading={isLoading}
          icon={<EmployeeIcon />}
          label={t('receipts.batch.payment-card.initiator')}
          value={
            <Typography fontSize={14} color="text.primary" textAlign="right">
              {initiatorName}
            </Typography>
          }
        />
      )}
      <Row
        isLoading={isLoading}
        icon={<DateIconOutline />}
        label={t('receipts.batch.payment-card.initiation-date')}
        value={
          <Stack direction="row">
            <Typography fontSize={14} color="text.primary">
              {fDate(payment.initiationDate, YEAR_MONTH_DAY_SHORT_FORMAT)}
              ,&nbsp;
            </Typography>

            <Typography fontSize={14} color="grey.600">
              {fTime(payment.initiationDate, TIME_FORMAT)}
            </Typography>
          </Stack>
        }
      />
      {payment.completedAt && (
        <Row
          isLoading={isLoading}
          icon={<DateIconFill />}
          label={t('receipts.batch.payment-card.completion-date')}
          value={
            <Stack direction="row">
              <Typography fontSize={14} color="text.primary">
                {fDate(payment.completedAt, YEAR_MONTH_DAY_SHORT_FORMAT)},&nbsp;
              </Typography>

              <Typography fontSize={14} color="grey.600">
                {fTime(payment.completedAt, TIME_FORMAT)}
              </Typography>
            </Stack>
          }
        />
      )}
    </Stack>
  );
};

interface RowProps {
  icon: ReactNode;
  label: ReactNode;
  value: ReactNode;
  isLoading?: boolean;
}

const Row: FC<RowProps> = ({ icon, label, value, isLoading }) => {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      gap={4}
    >
      <Stack direction="row" gap={1} alignItems="center">
        <Stack width={18}>{icon}</Stack>
        <OneLineTypography fontSize={14} color="text.primary">
          {label}
        </OneLineTypography>
      </Stack>
      {isLoading && (
        <Typography fontSize={14} color="text.primary">
          <Skeleton variant="text" width={100} />
        </Typography>
      )}
      {!isLoading && value}
    </Stack>
  );
};

const StatusIcon = (): JSX.Element => {
  return <Iconify sx={{ mt: '-2px' }} icon="mdi:cog" color="grey.600" />;
};

const DateIconOutline = (): JSX.Element => {
  return (
    <Iconify sx={{ mt: '-4px' }} icon="eva:calendar-outline" color="grey.600" />
  );
};

const DateIconFill = (): JSX.Element => {
  return (
    <Iconify sx={{ mt: '-4px' }} icon="eva:calendar-fill" color="grey.600" />
  );
};

const CashIcon = () => {
  return (
    <Iconify
      sx={{ mt: '-2px' }}
      icon="majesticons:euro-circle"
      color="grey.600"
    />
  );
};

const ReceiptIcon = () => {
  return (
    <Iconify sx={{ mt: '-1px' }} icon={'mdi:receipt-text'} color="grey.600" />
  );
};

const NursingHomeIcon = () => {
  return <Iconify sx={{ mt: '-2px' }} icon="mdi:home" color="grey.600" />;
};

const ServiceProviderIcon = () => {
  const theme = useTheme();
  return (
    <SvgIconStyle
      src={ServiceProviderSvgIcon}
      sx={{
        width: 16,
        height: 16,
        ml: '2px',
        mr: '-1px',
        mt: '-4px',
        color: theme.palette.grey['600'],
      }}
    />
  );
};

const EmployeeIcon = () => {
  return (
    <Iconify sx={{ mt: '-4px' }} icon="eva:person-fill" color="grey.600" />
  );
};

interface StatusLabel {
  state: PaymentStatus;
}

const StatusLabel: FC<StatusLabel> = ({ state }) => {
  const { t } = useTranslation();

  const color = useMemo(() => {
    switch (state) {
      case PaymentStatus.Pending:
        return 'info';
      case PaymentStatus.Completed:
        return 'success';
      case PaymentStatus.Failed:
        return 'error';
      case PaymentStatus.Disputed:
        return 'error';
      default:
        return 'default';
    }
  }, [state]);

  return (
    <Label color={color}>
      {t(`receipts.batch.payment-card.status.${state}`)}
    </Label>
  );
};
